import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Chart from "react-apexcharts";
import { observer } from "mobx-react";
import Lottie from "react-lottie";
import reportsStore from "../reportsStore";
import { userDetail } from "Stores/userDetail";
import dialerStoreData from "Stores/DialerData";
import Utils from "Utils/utils";
import EmptyReport from "Pages/Dashboard/reports/EmptyReport";
import NoActivityFound from "Pages/Dashboard/reports/NoActivityFound";
import BarChartLoader from "Utils/LottieFiles/BarChart.json";

const options = {
  chart: {
    type: "bar",
    height: 350,
    stacked: true,
    toolbar: {
      show: false
    },
    zoom: {
      enabled: false
    }
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        legend: {
          position: "bottom",
          offsetX: -10,
          offsetY: 0
        }
      }
    }
  ],
  yaxis: {
    labels: {
      formatter: value => {
        return Math.floor(value);
      }
    }
  },
  dataLabels: {
    enabled: false
  },
  plotOptions: {
    bar: {
      borderRadius: 0,
      horizontal: false
    }
  },
  legend: {
    position: "top",
    horizontalAlign: "right"
  },
  fill: {
    opacity: 1
  }
};

const CallDataInsightsWrapper = observer(() => {
  let location = useLocation();
  let contactAdded = userDetail?.userFeatureFlag?.noOfContactsAddedInTeam;
  let stageActiveList = [...dialerStoreData?.stageList || []].filter(
    item => item?.displayEnabled
  );

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: BarChartLoader
  };

  let series = [];
  let colors = [];

  let iteration = [...stageActiveList].forEach(ele => {
    const { displayName: name = "", color = "" } = ele || {};
    colors.push(color);
    series.push({ name, data: [] });
  });

  const [chartData, setChartData] = useState(series);
  const [chartOptions, setChartOptions] = useState(options);

  const constructChartData = (list = []) => {
    if (list) {
      const tempData = [...chartData];
      tempData.map(item => (item.data = []));
      const tempXaxisDate = [];
      list.forEach(item => {
        const { date = "", outcome = {} } = item || {};
        let tempList = [...stageActiveList].forEach((item, index) => {
          tempData[index]["data"].push(
            Math.ceil(outcome[item?.displayName] || 0)
          );
        });
        tempXaxisDate.push(date);
      });
      const {
        fromDate: fromDateTimestamp = "",
        toDate: toDateTimestamp = ""
      } = reportsStore?.dateFilter || {};
      
      // Convert timestamps to Date objects for formatting
      const fromDateObj = fromDateTimestamp ? new Date(fromDateTimestamp) : null;
      const toDateObj = toDateTimestamp ? new Date(toDateTimestamp) : null;
      
      let startDate = fromDateObj ? Utils.formatDateHypen(fromDateObj) : "";
      let endDate = toDateObj ? Utils.formatDateHypen(toDateObj) : "";
      const days = fromDateObj && toDateObj ? Utils.getDateDiffNumber(fromDateObj, toDateObj) : 0;
      const hasStartDate = tempXaxisDate.indexOf(startDate) !== -1;
      const hasEndDate = tempXaxisDate.indexOf(endDate) !== -1;
      list?.length &&
        days > 1 &&
        !hasStartDate &&
        tempXaxisDate.push(startDate);
      list?.length &&
        days > 1 &&
        !hasEndDate &&
        tempXaxisDate.splice(0, 0, endDate);
      tempData.map(item => {
        list?.length && days > 1 && !hasStartDate && item.data.push(0);
        list?.length && days > 1 && !hasEndDate && item.data.splice(0, 0, 0);
      });
      setChartOptions({
        ...options,
        xaxis: {
          type: days > 1 ? "datetime" : "category",
          categories:
            (days > 1 && tempXaxisDate) ||
            tempXaxisDate.map(item => Utils.formatDate(item)),
          labels: {
            datetimeFormatter: { month: "dd MMM" }
          }
        },
        plotOptions: {
          bar: {
            columnWidth: (days > 20 && "40%") || "20%"
          }
        },
        colors
      });
      setChartData(tempData);
    }
  };

  useEffect(() => {
    constructChartData(reportsStore.callsAnalyticsData);
  }, [reportsStore.callsAnalyticsData]);

  const loader = reportsStore.callsAnalyticsLoader;

  return (
    <div className="chartCard">
      <h4 className="cardTitle">Insights</h4>
      <div className="chartContent">
        {(!loader &&
          contactAdded &&
          chartData?.length &&
          chartData[0]?.data?.length && (
            <div className="stackedBarChart">
              <Chart
                options={chartOptions}
                series={chartData}
                type="bar"
                height={350}
              />
            </div>
          )) ||
          ""}
        {loader && (
          <div className="chartLoader">
            <Lottie
              options={defaultOptions}
              width={200}
              style={{ margin: "0 auto" }}
              speed={1}
            />
          </div>
        )}
        {!loader && !contactAdded && (
          <EmptyReport
            pathname={location?.pathname}
            className="emptyEmailInsight"
          />
        )}
        {(!loader && chartData?.length && !chartData[0]?.data?.length && (
          <NoActivityFound />
        )) ||
          ""}
      </div>
    </div>
  );
});

const CallsAnalyticsSection = observer(() => {
  useEffect(() => {
    // Fetch analytics data when component mounts or filters change
    if (reportsStore.dateFilter) {
      reportsStore.fetchCallsAnalytics();
    }
  }, [reportsStore.dateFilter, reportsStore.memberIds]);

  return (
    <div className="callsAnalytics">
      <div className="wrapCallsAnalyticsRow">
        <CallDataInsightsWrapper />
      </div>
    </div>
  );
});

export default CallsAnalyticsSection;
export { CallsAnalyticsSection };
